import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Check, Paperclip, X, HelpCircle, AlertCircle, MessageSquare, Hash, UserRound, Phone, Mail, FileText } from 'lucide-react';
import Header from '../Common/Header';
import Footer from '../Common/Footer';
import Input from '@ajat/design-system/Input';
import Button from '@ajat/design-system/Button';
import Hyperlink from '@ajat/design-system/Hyperlink';
import { RadioButtonMedium } from '@ajat/design-system/RadioButton';
import { pushNotification } from '@ajat/design-system/Notification/useNotification';
import { Sidebar, buildHelpTopics, useClaims } from '../../Common/HelpCenter';
import { CATEGORIES } from './faqData';
import { HELP_LABELS, HELP_ROUTES } from './helpConfig';
import '../../Common/HelpCenter/Claims.css';

const REASONS = [
  { id: 'size',     label: 'Fel storlek', sub: [
    { id: 'too-small', label: 'För liten' },
    { id: 'too-big',   label: 'För stor' },
  ] },
  { id: 'delivery', label: 'Leveransproblem', sub: [
    { id: 'late',             label: 'Försenad' },
    { id: 'missing',          label: 'Saknad vara' },
    { id: 'damaged-package',  label: 'Skadad förpackning' },
  ] },
  { id: 'quality',  label: 'Kvalitet eller tryck', sub: [
    { id: 'print',  label: 'Tryck' },
    { id: 'fabric', label: 'Tyg' },
    { id: 'seam',   label: 'Söm' },
    { id: 'other',  label: 'Annat' },
  ] },
  { id: 'payment',  label: 'Frågor om betalning', sub: null, requiresExtraInput: true, extraLabel: 'Beskriv betalningsproblemet' },
  { id: 'return',   label: 'Retur / ångra köp', sub: [
    { id: 'changed-mind', label: 'Ångrat köp' },
    { id: 'wrong-item',   label: 'Fel vara mottagen' },
    { id: 'other',        label: 'Annat' },
  ] },
  { id: 'other',    label: 'Annat', sub: null, requiresExtraInput: true, extraLabel: 'Beskriv vänligen orsaken' },
];

const RESOLUTIONS = [
  { id: 'refund',       label: 'Återbetalning' },
  { id: 'replacement',  label: 'Ny vara' },
];

const LABELS = {
  required:     'Obligatoriskt',
  invalidEmail: 'Ogiltig e-post',
  invalidPhone: 'Ogiltigt telefonnummer',
  fileTooLarge: 'Filen är för stor (max 5 MB)',
  submitError:  'Något gick fel. Försök igen.',
};

const ACCEPT = '.jpg,.jpeg,.png,.pdf';

const ABCSEClaims = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const claims = useClaims({
    labels: LABELS,
    reasons: REASONS,
    onSubmit: async ({ data }) => {
      await new Promise((r) => setTimeout(r, 600));
      // eslint-disable-next-line no-console
      console.log('[ABC-SE Claims] payload', data);
    },
  });

  useEffect(() => {
    const prev = document.documentElement.getAttribute('data-brand');
    document.documentElement.setAttribute('data-brand', 'ABC');
    return () => {
      if (prev) document.documentElement.setAttribute('data-brand', prev);
      else document.documentElement.removeAttribute('data-brand');
    };
  }, []);

  useEffect(() => {
    if (claims.submitted) {
      pushNotification({
        type: 'success',
        title: 'Reklamation mottagen',
        message: 'Vi återkommer så snart vi kan via e-post.',
      });
    }
  }, [claims.submitted]);

  const handleFilePick = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const file of files) {
      if (claims.data.files.length + 1 > claims.MAX_FILES) break;
      try { await claims.addFile(file); }
      catch (err) { pushNotification({ type: 'error', title: 'Fel', message: err.message }); }
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const reasonConfig = claims.reasonConfig;

  const topics = buildHelpTopics({
    context: 'claims',
    categories: CATEGORIES,
    labels: HELP_LABELS,
    routes: HELP_ROUTES,
    navigate,
  });

  return (
    <div className="helpcenter">
      <Header showTopBar={false} />
      <main className="helpcenter__main">
        <div className="helpcenter__container">
          <div className="helpcenter__list-col">
            <h1 className="helpcenter__title typo-h1">Reklamation</h1>
            <p className="helpcenter__lede typo-body-lg">
              Beskriv problemet, så hjälper vi dig så snart vi kan. Du hittar ditt ordernummer i bekräftelsemejlet.
            </p>
          {claims.submitted ? (
            <div className="helpcenter__success">
              <span className="helpcenter__success-icon"><Check size={28} /></span>
              <h2 className="helpcenter__success-title typo-h2">Tack för din reklamation</h2>
              <p className="helpcenter__success-text typo-body">
                Vi har mottagit ditt ärende och återkommer via e-post inom 1–2 vardagar.
              </p>
              <div className="helpcenter__success-ctas">
                <Button variant="black" size="secondary" iconRight={<ArrowRight size={18} />} onClick={() => navigate('/clothing/abc-se')}>
                  Tillbaka till startsidan
                </Button>
                <Button variant="outline" size="secondary" onClick={() => claims.reset()}>
                  Skicka en ny reklamation
                </Button>
              </div>
            </div>
          ) : (
            <form className="claims-page__card" onSubmit={claims.handleSubmit} noValidate>
              <div className="claims-page__section">
                <h2 className="claims-page__section-title typo-h3">1. Vad gäller det?</h2>
                <Input
                  type="dropdown"
                  label="Välj orsak"
                  icon={HelpCircle}
                  value={reasonConfig?.label || ''}
                  options={REASONS.map((r) => ({ value: r.id, label: r.label }))}
                  onSelect={(opt) => { claims.setField('reason', opt ? opt.value : ''); claims.setField('subReason', ''); claims.setField('reasonExtra', ''); }}
                  onBlur={claims.handleBlur('reason')}
                  onFocus={claims.handleFocus('reason')}
                  error={claims.fieldError('reason')}
                />
                {reasonConfig?.sub ? (
                  <Input
                    type="dropdown"
                    label="Välj detalj"
                    icon={AlertCircle}
                    value={reasonConfig.sub.find((s) => s.id === claims.data.subReason)?.label || ''}
                    options={reasonConfig.sub.map((s) => ({ value: s.id, label: s.label }))}
                    onSelect={(opt) => claims.setField('subReason', opt ? opt.value : '')}
                    onBlur={claims.handleBlur('subReason')}
                    onFocus={claims.handleFocus('subReason')}
                    error={claims.fieldError('subReason')}
                  />
                ) : null}
                {reasonConfig?.requiresExtraInput ? (
                  <Input
                    type="text"
                    label={reasonConfig.extraLabel || 'Beskriv kort'}
                    icon={MessageSquare}
                    value={claims.data.reasonExtra}
                    onChange={claims.update('reasonExtra')}
                    onBlur={claims.handleBlur('reasonExtra')}
                    onFocus={claims.handleFocus('reasonExtra')}
                    error={claims.fieldError('reasonExtra')}
                  />
                ) : null}
              </div>

              <div className="claims-page__section">
                <h2 className="claims-page__section-title typo-h3">2. Din order</h2>
                <Input
                  type="text"
                  label="Ordernummer"
                  icon={Hash}
                  value={claims.data.orderNumber}
                  onChange={claims.update('orderNumber')}
                  onBlur={claims.handleBlur('orderNumber')}
                  onFocus={claims.handleFocus('orderNumber')}
                  error={claims.fieldError('orderNumber')}
                  helperText="Finns i din orderbekräftelse via e-post."
                />
              </div>

              <div className="claims-page__section">
                <h2 className="claims-page__section-title typo-h3">3. Kontaktuppgifter</h2>
                <div className="claims-page__row claims-page__row--two">
                  <Input
                    type="text"
                    label="Namn"
                    icon={UserRound}
                    value={claims.data.name}
                    onChange={claims.update('name')}
                    onBlur={claims.handleBlur('name')}
                    onFocus={claims.handleFocus('name')}
                    error={claims.fieldError('name')}
                  />
                  <Input
                    type="text"
                    label="Telefon"
                    icon={Phone}
                    value={claims.data.phone}
                    onChange={claims.update('phone')}
                    onBlur={claims.handleBlur('phone')}
                    onFocus={claims.handleFocus('phone')}
                    error={claims.fieldError('phone')}
                  />
                </div>
                <Input
                  type="text"
                  label="E-post"
                  icon={Mail}
                  value={claims.data.email}
                  onChange={claims.update('email')}
                  onBlur={claims.handleBlur('email')}
                  onFocus={claims.handleFocus('email')}
                  error={claims.fieldError('email')}
                />
              </div>

              <div className="claims-page__section">
                <h2 className="claims-page__section-title typo-h3">4. Önskad lösning</h2>
                <p className="claims-page__section-text typo-body-sm">
                  Vi gör vårt bästa för att möta ditt önskemål, men kan i vissa fall föreslå ett alternativ.
                </p>
                <div className="claims-page__radio-group">
                  {RESOLUTIONS.map((r) => (
                    <RadioButtonMedium
                      key={r.id}
                      name="preferredResolution"
                      value={r.id}
                      title={r.label}
                      checked={claims.data.preferredResolution === r.id}
                      onChange={() => { claims.setField('preferredResolution', r.id); }}
                      onBlur={claims.handleBlur('preferredResolution')}
                    />
                  ))}
                </div>
                {claims.fieldError('preferredResolution') ? (
                  <p className="claims-page__error typo-body-sm">{claims.fieldError('preferredResolution')}</p>
                ) : null}
              </div>

              <div className="claims-page__section">
                <h2 className="claims-page__section-title typo-h3">5. Beskriv problemet</h2>
                <Input
                  type="textarea"
                  label="Beskrivning"
                  icon={FileText}
                  rows={5}
                  value={claims.data.details}
                  onChange={claims.update('details')}
                  onBlur={claims.handleBlur('details')}
                  onFocus={claims.handleFocus('details')}
                  error={claims.fieldError('details')}
                />
              </div>

              <div className="claims-page__section">
                <h2 className="claims-page__section-title typo-h3">6. Bifoga bilder eller filer</h2>
                <p className="claims-page__file-help typo-body-sm">
                  Max {claims.MAX_FILES} filer × 5 MB. JPG, PNG eller PDF.
                </p>
                <input ref={fileInputRef} type="file" accept={ACCEPT} multiple hidden onChange={handleFilePick} />
                <div className="claims-page__files">
                  {claims.data.files.length > 0 ? (
                    <div className="claims-page__file-list">
                      {claims.data.files.map((f) => (
                        <div key={f.id} className="claims-page__file-row">
                          <Paperclip size={18} />
                          <span className="claims-page__file-name typo-body">{f.name}</span>
                          <Button
                            size="tertiary"
                            variant="text"
                            iconOnly
                            iconLeft={<X size={18} />}
                            onClick={() => claims.removeFile(f.id)}
                            aria-label="Ta bort fil"
                          />
                        </div>
                      ))}
                    </div>
                  ) : null}
                  {claims.data.files.length < claims.MAX_FILES ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="secondary"
                      iconLeft={<Paperclip size={18} />}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      Bifoga fil
                    </Button>
                  ) : null}
                </div>
              </div>

              <div className="claims-page__section">
                <label className="claims-page__checkbox">
                  <input
                    type="checkbox"
                    className="claims-page__checkbox-input"
                    checked={claims.data.gdpr}
                    onChange={(e) => { claims.setField('gdpr', e.target.checked); }}
                    onBlur={claims.handleBlur('gdpr')}
                  />
                  <span className="claims-page__checkbox-text typo-body-sm">
                    Jag godkänner att ABC-Gruppen behandlar mina personuppgifter i samband med mitt ärende.
                    {' '}
                    <Hyperlink href="#" variant="small">Läs personuppgiftspolicy</Hyperlink>
                  </span>
                </label>
                {claims.fieldError('gdpr') ? (
                  <p className="claims-page__checkbox-error typo-body-sm">{claims.fieldError('gdpr')}</p>
                ) : null}
              </div>

              <div className="claims-page__submit-row">
                {claims.submitError ? (
                  <p className="claims-page__error typo-body-sm">{claims.submitError}</p>
                ) : null}
                <Button
                  type="submit"
                  variant="black"
                  size="primary"
                  iconRight={<ArrowRight size={20} />}
                  loading={claims.submitting}
                  disabled={claims.submitting}
                >
                  Skicka reklamation
                </Button>
              </div>
            </form>
          )}
          </div>

          <Sidebar
            title={HELP_LABELS.sidebarTitle}
            topics={topics}
            activeId="claims"
            dividerBeforeId="claims"
          />
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ABCSEClaims;
